import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const sixDimensions = [
  { label: '政治方向', icon: 'ri-compass-3-line', desc: '方向准不准', color: 'primary' },
  { label: '理论阐释', icon: 'ri-book-open-line', desc: '理论深不深', color: 'accent' },
  { label: '问题意识', icon: 'ri-question-answer-line', desc: '问题强不强', color: 'primary' },
  { label: '案例使用', icon: 'ri-article-line', desc: '案例贴不贴', color: 'accent' },
  { label: '课堂互动', icon: 'ri-chat-smile-2-line', desc: '互动有没有发生', color: 'primary' },
  { label: '价值引导', icon: 'ri-lightbulb-flash-line', desc: '价值是否自然进入', color: 'accent' },
];

export default function P9Complexity({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 背景光晕 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/15 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-12 w-72 h-72 rounded-full bg-accent-100/10 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-4">
          <span className={`inline-block text-xs font-label font-semibold text-accent-600 uppercase tracking-[0.12em] mb-1 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            评价复杂性
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            为什么<span className="text-primary-500">思政课评课</span>最难？
          </h2>
          <p className={`text-sm text-foreground-600 font-body transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            思政课评价的不是一个点，而是一组<strong className="text-foreground-800">交织在一起</strong>的教学行为
          </p>
        </div>

        {/* 六维度 */}
        <div className={`grid grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {sixDimensions.map((dim, i) => {
            const isGold = dim.color === 'accent';
            return (
              <div
                key={dim.label}
                className={`rounded-xl transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${350 + i * 100}ms` }}
              >
                <div className={`relative h-full p-3 ${isGold ? 'glass-liquid-gold' : 'glass-liquid'} border rounded-xl`}>
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isGold
                          ? 'bg-accent-100/80 border border-accent-300/40'
                          : 'bg-primary-50 border border-primary-200/40'
                      }`}
                    >
                      <i className={`${dim.icon} text-base ${isGold ? 'text-accent-600' : 'text-primary-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="font-heading text-sm font-bold text-foreground-800">{dim.label}</h4>
                        <span className={`text-[10px] font-label font-bold px-1.5 py-0.5 rounded-full ${isGold ? 'bg-accent-100 text-accent-700' : 'bg-primary-50 text-primary-700'}`}>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 font-body leading-snug">{dim.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 对比区 */}
        <div className={`grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-4 items-stretch mb-3 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 左侧：知识技能型课程 */}
          <div className="rounded-xl overflow-hidden">
            <div className="h-full glass-blue border border-primary-200/40 p-4 md:p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-primary-100 border border-primary-200/40 flex items-center justify-center">
                  <i className="ri-check-double-line text-sm text-primary-600" />
                </div>
                <span className="text-xs font-label font-semibold text-primary-600 uppercase tracking-wider">传统课程</span>
              </div>
              <h4 className="font-heading text-base md:text-lg font-bold text-foreground-800 mb-3">知识技能型课程</h4>
              <div className="flex-1 space-y-2">
                {['答案是否正确', '过程是否清楚', '练习反馈是否有效'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-primary-50 border border-primary-200/40 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-[9px] text-primary-500" />
                    </div>
                    <span className="text-xs font-body text-foreground-600">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-primary-200/30 flex items-center gap-2">
                <span className="text-xl font-heading font-bold text-primary-600">3</span>
                <span className="text-xs font-body text-foreground-500">个评价维度</span>
                <span className="text-xs font-label text-primary-500 bg-primary-50 px-1.5 py-0.5 rounded-full border border-primary-200/30">线性评价</span>
              </div>
            </div>
          </div>

          {/* 中间 VS */}
          <div className="flex flex-row md:flex-col items-center justify-center gap-2 py-1 md:py-0">
            <div className="w-8 h-8 rounded-full bg-accent-100 border border-accent-300/40 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm text-accent-600" />
            </div>
            <div className="hidden md:block w-px h-8 bg-gradient-to-b from-accent-300/30 via-accent-400/50 to-accent-300/30" />
            <div className="hidden md:block w-px h-8 bg-gradient-to-b from-primary-300/30 via-primary-400/50 to-primary-300/30" />
            <div className="w-8 h-8 rounded-full bg-primary-100 border border-primary-300/40 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm text-primary-600" />
            </div>
          </div>

          {/* 右侧：思政课 */}
          <div className="rounded-xl overflow-hidden">
            <div className="h-full glass-liquid-gold border border-accent-200/40 p-4 md:p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-accent-100 border border-accent-300/40 flex items-center justify-center">
                  <i className="ri-puzzle-line text-sm text-accent-600" />
                </div>
                <span className="text-xs font-label font-semibold text-accent-600 uppercase tracking-wider">复杂课程</span>
              </div>
              <h4 className="font-heading text-base md:text-lg font-bold text-foreground-800 mb-3">思政课</h4>
              <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1.5">
                {sixDimensions.map((dim) => (
                  <div key={dim.label} className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-accent-100/80 border border-accent-300/30 flex items-center justify-center flex-shrink-0">
                      <i className={`${dim.icon} text-[8px] text-accent-600`} />
                    </div>
                    <span className="text-xs font-body text-foreground-600">{dim.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-accent-200/30 flex items-center gap-2">
                <span className="text-xl font-heading font-bold text-accent-600">6</span>
                <span className="text-xs font-body text-foreground-500">个交织维度</span>
                <span className="text-xs font-label text-accent-600 bg-accent-50 px-1.5 py-0.5 rounded-full border border-accent-200/30">网状评价</span>
              </div>
            </div>
          </div>
        </div>

        {/* 底部洞察条 */}
        <div className={`flex justify-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl glass border border-background-200/60">
            <div className="w-7 h-7 rounded-lg bg-primary-50 border border-primary-200/40 flex items-center justify-center flex-shrink-0">
              <i className="ri-links-line text-sm text-primary-500" />
            </div>
            <span className="text-xs md:text-sm font-body text-foreground-600">
              AI评课必须先把这些维度<strong className="text-foreground-800">结构化</strong>，才能实现可信评价
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}